<template>
  <div class="review-summary p-24">
    <div class="flex">
      <a-input-search
        style="width: 300px"
        placeholder="请输入投标单位名称"
        enter-button
        class="m-r-24"
        @search="handleSearch"
      />
      <el-button v-if="showReStart" type="primary" @click="reStartReview">
        重新评审
      </el-button>
    </div>
    <el-table :data="tableData" border class="m-t-24">
      <el-table-column label="序号" type="index" width="60" align="center" />
      <el-table-column
        v-for="head in tableHead"
        :key="head.label"
        :label="head.label"
        align="center"
      >
        <template v-slot:default="{ row }">
          <span :class="getTdContent(head, row).className">
            {{ getTdContent(head, row).content }}
          </span>
        </template>
      </el-table-column>
    </el-table>
    <pagination
      v-if="total > 0"
      :total="total"
      :page.sync="page"
      :limit.sync="size"
      @pagination="getTableData"
    />

    <div v-if="showFoot" class="summary-foot">
      <el-button
        type="primary"
        :disabled="summaryDisabled"
        @click="submitReviewSummary"
      >
        确认汇总
      </el-button>
    </div>
  </div>
</template>

<script>
import Pagination from '@/components/common/Pagination.vue'
import {
  getPurchaseReviewExpertSummaryPageApi,
  reStartPurchaseReviewWayApi,
  submitPurchaseReviewSummaryApi
} from '@/api/procure/purchase-review'
import {
  onlineReviewMethodDict,
  onlineReviewScoreStatusDict,
  onlineReviewTypeDict
} from '@/dictionary/project/online-review'

/**
 * 评审办法汇总（表头是专家）
 */
export default {
  name: 'OnlineReviewMemberSummary',

  components: { Pagination },

  props: {
    reviewId: {
      type: [String, Number],
      default: ''
    },

    orderId: {
      type: [String, Number],
      default: ''
    },

    reviewWay: {
      type: Object,
      default: () => {}
    },

    // 评审专家列表
    reviewerList: {
      type: Array,
      default: () => []
    },

    allSubmitted: {
      type: Boolean,
      default: false
    },

    reviewClosed: {
      type: Boolean,
      default: false
    },

    // 是否终止评审
    isReviewClose: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // 表格数据
      tableData: [],
      page: 1,
      size: 10,
      total: 0,

      // 列表筛选表单
      filterForm: {
        // 报价单位名称
        supplierName: ''
      }
    }
  },

  computed: {
    // 表头
    tableHead() {
      return [
        { label: '报价单位' },
        ...(this.reviewerList || []).map(item => {
          return { label: item.expertName, id: item.reviewerId }
        })
      ]
    },

    // 当前评审办法是打分型评审
    isScoreMethod() {
      return this.reviewWay?.reviewMethod === onlineReviewMethodDict.SCORE.value
    },

    // 是否显示底部操作区
    showFoot() {
      // 评审办法已汇总不显示
      if (
        this.reviewWay.summaryStatus ===
        onlineReviewScoreStatusDict.COMPLETED.value
      ) {
        return false
      }
      return true
    },

    wayLabel() {
      return onlineReviewTypeDict.translate(this.reviewWay.wayType)?.label
    },

    // 评审办法已汇总
    reviewCompleted() {
      return (
        this.reviewWay.summaryStatus ===
        onlineReviewScoreStatusDict.COMPLETED.value
      )
    },

    // 是否显示评审办法汇总
    showReStart() {
      // 已汇总后显示
      return this.reviewCompleted && !this.reviewClosed
    },

    // 不可以汇总
    summaryDisabled() {
      return !this.allSubmitted
    }
  },

  watch: {
    reviewWay: {
      handler(val) {
        if (val?.wayId) {
          this.getTableData()
        }
      },
      immediate: true,
      deep: true
    }
  },

  methods: {
    /**
     * 获取单元格内容
     */
    getTdContent(head, row) {
      if (head.id) {
        if (this.isScoreMethod) {
          return { content: row.summaryItemMap[head.id] || '-' }
        }
        return row.summaryItemMap[head.id]
          ? { content: '通过' }
          : { content: '不通过', className: 'text-danger' }
      }
      if (head.label === '报价单位') {
        return { content: row.supplierName || '-' }
      }
      return { content: '-' }
    },

    /**
     * 获取表格内容
     */
    getTableData() {
      const params = {
        wayId: this.reviewWay.wayId,
        page: this.page,
        size: this.size,
        ...this.filterForm
      }
      getPurchaseReviewExpertSummaryPageApi(this.reviewId, params)
        .then(res => {
          const _data = res.data || {}
          this.tableData = (_data.records || []).map(item => {
            item.summaryItemMap = {}
            item.summaryItems.forEach(expert => {
              item.summaryItemMap[expert.reviewerId] = expert.score
            })
            delete item.summaryItems
            return item
          })
          this.total = _data.total || 0
        })
        .catch(err => {
          if (err?.message) this.$message.error(err.message)
        })
    },

    handleSearch(value) {
      this.filterForm.supplierName = value
      this.getTableData()
    },

    /**
     * 提交汇总
     */
    submitReviewSummary() {
      if (this.isReviewClose) {
        this.$message.warning('该评审已终止，操作失败')
        return
      }
      this.$systemConfirm('确定提交汇总吗？').then(bool => {
        if (!bool) return
        submitPurchaseReviewSummaryApi(this.reviewId, {
          wayId: this.reviewWay.wayId
        })
          .then(res => {
            this.$emit('afterSummary')
          })
          .catch(err => {
            if (err?.message) this.$message.error(err.message)
          })
      })
    },

    /**
     * 重新评审
     */
    reStartReview() {
      this.$systemConfirm(`确定重新评审${this.wayLabel}吗？`).then(result => {
        if (!result) return
        reStartPurchaseReviewWayApi(this.reviewId, {
          wayId: this.reviewWay.wayId
        })
          .then(res => {
            this.$emit('afterReStart')
          })
          .catch(err => {
            if (err?.message) this.$message.error(err.message)
          })
      })
    }
  }
}
</script>

<style scoped lang="scss">
.review-summary {
  min-height: 580px;
}
.summary-foot {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background: $white-color;
  box-shadow: 0px -3px 6px rgba(0, 0, 0, 0.06);
  border: 1px solid $info-fifth-color;
  height: 56px;
  text-align: center;
  padding: 12px 0;
}
</style>
